import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Подтвердить', 
  cancelText = 'Отмена',
  variant = 'danger' // 'danger', 'warning', 'info'
}) => {
  const firstButtonRef = useRef(null);
  const lastButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen && firstButtonRef?.current) {
      firstButtonRef?.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }
    };

    const handleTab = (e) => {
      if (e?.key !== 'Tab') return;

      const focusableElements = [firstButtonRef?.current, lastButtonRef?.current];
      const firstElement = focusableElements?.[0];
      const lastElement = focusableElements?.[focusableElements?.length - 1];

      if (e?.shiftKey) {
        if (document.activeElement === firstElement) {
          e?.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e?.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen, onClose]);

  const variantStyles = {
    danger: {
      icon: 'AlertCircle',
      iconColor: 'var(--color-error)',
      iconBg: 'bg-error/10'
    },
    warning: {
      icon: 'AlertTriangle',
      iconColor: 'var(--color-warning)',
      iconBg: 'bg-warning/10'
    },
    info: {
      icon: 'Info',
      iconColor: 'var(--color-info)',
      iconBg: 'bg-info/10'
    }
  };

  const currentVariant = variantStyles?.[variant] || variantStyles?.danger;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            aria-hidden="true" />

          {/* Modal */}
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby="modal-description">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-xl border border-border shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e?.stopPropagation()}>
              
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-full ${currentVariant?.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon 
                    name={currentVariant?.icon} 
                    size={24} 
                    color={currentVariant?.iconColor} 
                    aria-hidden="true" />
                </div>
                
                <div className="flex-1">
                  <h2 
                    id="modal-title"
                    className="text-xl font-heading font-bold text-foreground mb-2">
                    {title}
                  </h2>
                  <p 
                    id="modal-description"
                    className="text-sm text-muted-foreground">
                    {message}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 justify-end mt-6">
                <Button
                  ref={lastButtonRef}
                  variant="outline"
                  size="default"
                  onClick={onClose}>
                  {cancelText}
                </Button>
                
                <Button
                  ref={firstButtonRef}
                  variant={variant === 'danger' ? 'destructive' : 'default'}
                  size="default"
                  onClick={onConfirm}>
                  {confirmText}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;